import clsx from 'clsx';
import * as React from 'react';

import {SvgIconReact} from 'common/components/svg-icon';
import {formatTextReact} from 'common/util/format-text-react';
import {withStyles} from 'common/util/react';
import timing from 'common/stylesheets/mixins/timing.json';
import type {SvgIconType} from 'common/components/svg-icon/types';

import {ToastActionEvent, ToastProps, ToastType} from './types';
import css from './index.module.styl?inline';
import styles from './index.module.styl';

const TOAST_TYPE_ICONS: Record<ToastType, SvgIconType> = {
  [ToastType.ERROR]: `warning`,
  [ToastType.INFO]: `info`,
  [ToastType.SUCCESS]: `circle-checkmark`,
  [ToastType.WARNING]: `warning`,
  [ToastType.DESTRUCTIVE]: `trashcan`,
};

const TYPE_CLASSES: Record<ToastType, string> = {
  [ToastType.ERROR]: styles.error,
  [ToastType.INFO]: styles.info,
  [ToastType.SUCCESS]: styles.success,
  [ToastType.WARNING]: styles.warning,
  [ToastType.DESTRUCTIVE]: styles.destructive,
};

const DELAY_REMOVE_MS = 2 * (timing as Record<string, number>)[`$mp-slow`];

function Toast({
  cta,
  ctaHref,
  ctaTarget,
  elref,
  hide,
  hideCancelButton,
  message,
  timeout,
  type = ToastType.INFO,
  undoable,
  undoLabel,
  onAction,
  onClose,
  onUndo,
}: ToastProps) {
  const [shown, setShown] = React.useState(true);

  React.useEffect(() => {
    if (!hide || !shown) {
      return undefined;
    }
    const handle = window.setTimeout(() => setShown(false), DELAY_REMOVE_MS);
    return () => window.clearTimeout(handle);
  }, [hide, shown]);

  React.useEffect(() => {
    if (!timeout) {
      return undefined;
    }
    const handle = window.setTimeout(() => onClose?.(), timeout);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot timer, must not reset on onClose identity change
  }, [timeout]);

  const fireWithDefaultClose = (handler?: (ev: ToastActionEvent) => void) => {
    let prevented = false;
    handler?.({preventDefault: () => (prevented = true)});
    window.setTimeout(() => {
      if (!prevented) {
        onClose?.();
      }
    }, 0);
  };

  const handleCta = (ev: React.MouseEvent) => {
    if (!ctaHref) {
      ev.preventDefault();
    }
    fireWithDefaultClose(onAction);
  };

  const handleUndo = () => fireWithDefaultClose(onUndo);
  const handleClose = () => onClose?.();

  const typeClass = TYPE_CLASSES[type];

  return (
    <div className={styles.mpToastStage} elref={elref}>
      {shown && (
        <div className={clsx(styles.mpToastContainer, {[styles.mpToastOut]: hide})}>
          <div className={styles.mpToastWidget}>
            <div className={clsx(styles.iconContainer, typeClass, {[styles.timeout]: !!timeout})}>
              <SvgIconReact icon={TOAST_TYPE_ICONS[type]} className={styles.iconContainerIcon} />
              {timeout ? (
                <div
                  className={clsx(styles.iconContainerAnimatedFadeout, typeClass)}
                  style={{animationDuration: `${timeout}ms`}}
                />
              ) : null}
            </div>
            <div className={styles.mpToastBody}>
              <div className={styles.mpToastMessage}>
                {typeof message === `string` ? formatTextReact(message) : message}
              </div>
              <div className={styles.mpToastActions}>
                {cta ? (
                  <a
                    className={clsx(styles.mpToastAction, styles.mpToastCta)}
                    href={ctaHref || undefined}
                    target={ctaHref ? ctaTarget : undefined}
                    onClick={handleCta}
                  >
                    {cta}
                  </a>
                ) : null}
                {undoable ? (
                  <div className={styles.mpToastAction} onClick={handleUndo}>
                    {undoLabel || `Undo`}
                  </div>
                ) : null}
              </div>
              {!hideCancelButton ? (
                <div className={styles.mpToastClose} onClick={handleClose}>
                  <SvgIconReact icon='x' className={styles.mpToastCloseIcon} />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export const ToastReact = withStyles(Toast, css);
